import * as React from 'react';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';

import { IS_BROWSER } from '@src/config/settings';
import { useCreateSurveyMutation } from '@src/generated/hooks';
import { useApp } from '@src/hooks/useApp';
import { useSurvey } from '@src/hooks/useSurvey';
import { selectSurveyState } from '@src/recoil/selectors/surveyState';
import { SurveyBack } from '@src/services/surveys/components/SurveyBack';
import { SurveyButton } from '@src/services/surveys/components/SurveyButton';
import { SurveyDebugger } from '@src/services/surveys/components/SurveyDebugger';
import { SurveyHeading } from '@src/services/surveys/components/SurveyHeading';
import { SurveyProgress } from '@src/services/surveys/components/SurveyProgress';
import { SurveyQuestion } from '@src/services/surveys/components/SurveyQuestion';
import { validateQuestionType } from '@src/services/surveys/utils/validation';
import {
  checkAnalytics,
  trackingSurveyCompleted,
  trackingSurveyStart
} from '@src/utils/tracking';

export interface SurveyRendererProps {}

/**
 * @name SurveyRenderer
 * @description This component is responsible for rendering out an entire
 * survey flow, handling the basic navigation, validation, and submission.
 */
export const SurveyRenderer = (_props: SurveyRendererProps) => {
  // Hooks
  const formRef = React.useRef<HTMLFormElement>(null);
  const { toggleLoader } = useApp();
  const { asPath, push, query } = useRouter();
  const { errors, valid } = useRecoilValue(selectSurveyState);
  const { id: _id, question: _q, ...params } = query;
  const { answers, atom, error, initialize, mixpanel, questions, resetSurvey, setError } = useSurvey(); // prettier-ignore
  const [createSurvey] = useCreateSurveyMutation();

  // Setup
  const base = asPath.split('?')[0];
  const current: number = _q ? Number(_q) : 1;
  const question = questions.active[current - 1] || questions.active[0];

  // Handlers
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const isSubmit = current === total;
    const errorMsg = validateQuestionType(question, answers);

    event.preventDefault();
    setError(undefined);

    if (errorMsg) {
      setError(errorMsg);

      return;
    }

    // We're moving forward through the survey "next"
    if (!isSubmit) {
      const urlParams = new URLSearchParams({
        ...params,
        question: (current + 1).toString()
      });
      push(`${base}?${urlParams.toString()}`);
    }

    // We're ready to submit it!
    if (isSubmit) {
      if (valid) {
        toggleLoader(true);

        const { data, errors } = await createSurvey({
          variables: {
            input: {
              data: answers,
              id: atom.survey.id,
              version: atom.survey.version
            }
          }
        });

        if (errors) {
          Sentry.captureException(errors, answers);
          setError('Failed to submit the survey, please try again later.');
          toggleLoader(false);
        }

        const referredBy = answers['referredBy'] ?? '';
        const surveyId = data?.createSurvey.id ?? '';

        trackingSurveyCompleted(atom.survey.id, atom.survey.version, mixpanel);
        resetSurvey();

        push({
          pathname: '/surveys/post-survey',
          query: { ...params, id: atom.survey.id, referredBy, surveyId }
        });
      } else {
        alert(`Please correct the following errors: ${errors.join(',')}`);

        /**
         * Head to the first question with an error, we probably want to do
         * this as we mount the survey as well, to try and prevent this from
         * jumping the user back to the first question of the survey
         */
        const urlParams = new URLSearchParams({
          ...params,
          question: errors[0].toString()
        });
        push(`${base}?${urlParams.toString()}`);
      }
    }
  };

  // Life Cycle

  /**
   * @description Re-focus on the the form element as the current
   * question changes.
   */
  React.useEffect(() => {
    return () => {
      if (formRef.current) formRef.current.focus();
    };
  }, [current]);

  /**
   * @description Reset any error(s) as we navigate through the survey
   */
  React.useEffect(() => {
    setError(undefined);
  }, [current]);

  /**
   * @description Track survey progress in mixpanel
   */
  React.useEffect(() => {
    mixpanel.track(question.name, {
      url: atom.survey.id,
      version: atom.survey.version
    });
  }, [current]);

  /**
   * @description Ensure our "survey_start" fires (best we can)
   */
  React.useEffect(() => {
    (async () => {
      if (!IS_BROWSER) return;

      const isLoaded = await checkAnalytics();
      if (isLoaded) {
        trackingSurveyStart(atom.survey.id, atom.survey.version, mixpanel);
      }
    })();
  }, [IS_BROWSER]);

  /**
   * @description We initialize the survey here as this component mounts.
   * @note ⚠️ Order matters ⚠️  "initialize()" must be the last survey hook
   */
  React.useEffect(() => {
    initialize();
  }, []);

  // 🔌 Short Circuit
  if (!question || question === null) return null;

  // Setup
  const isProgress = question.type === 'progress';
  const properties = question.properties ?? {};
  const total = questions.active.length;
  const { isWide = false } = properties;

  const tailwind = `px-4 mb-20 mx-auto w-full flex-grow pb-10 md:px-0 md:pb-4 md:mb-10`;
  const cssQuestion = classnames(tailwind, {
    'max-w-4xl': isWide,
    'max-w-lg': !isWide
  });

  // Markup
  const renderHeading = () => {
    if (isProgress) return null;

    return (
      <div className="m-auto my-16 max-w-3xl md:mb-14 md:mt-20">
        <SurveyHeading question={question} />
      </div>
    );
  };

  return (
    <>
      <SurveyProgress current={current} total={total} />
      {<SurveyBack />}

      <form
        className="m-auto flex flex-col text-center outline-none"
        onSubmit={onSubmit}
        ref={formRef}
        tabIndex={0}
      >
        {renderHeading()}

        <div className={cssQuestion}>
          <SurveyQuestion question={question} />
          {error && (
            <p
              className="mt-2 text-right font-central-no1-light text-14 md:text-16 lg:text-18"
              dangerouslySetInnerHTML={{ __html: error }}
              style={{ color: '#d0021b' }}
            />
          )}
        </div>

        <div className="left fixed bottom-0 flex w-full justify-center gap-4 bg-turmeric p-4 md:relative md:mb-10 md:bg-transparent md:pt-0">
          <SurveyButton
            className="z-10 flex-1 px-4 py-2 text-base md:flex-none md:px-8 md:py-4 md:text-lg"
            cta={question.properties?.cta}
            current={current}
            total={total}
          />
        </div>
      </form>

      <SurveyDebugger />
    </>
  );
};
